// Favori filmler ve izleme durumu yönetimi servisis
import {
    saveFavoriteToSupabase,
    removeFavoriteFromSupabase,
    saveWatchedToSupabase as saveWatchedToSupabaseTable,
    removeWatchedFromSupabase
} from './supabaseDataService';

// Favori filmler localStorage anahtarları (user.id ile kapsamlı)
const FAVORITES_KEY_BASE = 'film_favorites';
const WATCHED_KEY_BASE = 'film_watched';
const RECOMMENDATIONS_KEY_BASE = 'film_recommendations';

// Kullanıcıya özel localStorage anahtarı oluştur
const getUserScopedKey = (baseKey) => {
    const userId = window.currentUserId || null;
    if (!userId) {
        // Eski sistem uyumluluğu için fallback (sessizce)
        return baseKey;
    }
    return `${baseKey}:${userId}`;
};

// Kullanıcıya özel anahtarlar
const getFavoritesKey = () => getUserScopedKey(FAVORITES_KEY_BASE);
const getWatchedKey = () => getUserScopedKey(WATCHED_KEY_BASE);
const getRecommendationsKey = () => getUserScopedKey(RECOMMENDATIONS_KEY_BASE);

// Supabase'e favorileri kaydet
const saveFavoritesToSupabase = async (favorites) => {
    try {
        // AuthContext'ten updateUserPreferences fonksiyonuna eriş
        if (window.updateUserPreferences && typeof window.updateUserPreferences === 'function') {
            await window.updateUserPreferences({ favorites });
        }
    } catch (error) {
        console.error('❌ Supabase favori kaydetme hatası:', error);
        // Hata olsa bile localStorage'da zaten kayıtlı, sessizce devam et
    }
};

// Supabase'e izlenenleri kaydet (supabaseDataService üzerinden)
// Favori film ekleme (robust versiyonunu kullan)
export const addToFavorites = async (movie) => {
    return await addToFavoritesRobust(movie);
};

// Favorilerden çıkarma (robust versiyonunu kullan)
export const removeFromFavorites = async (movieId, movieTitle = null) => {
    return await removeFromFavoritesRobust(movieId, movieTitle);
};

// Tüm favorileri getir (user.id ile kapsamlı)
export const getFavorites = () => {
    try {
        const key = getFavoritesKey();
        const favorites = localStorage.getItem(key);
        return favorites ? JSON.parse(favorites) : [];
    } catch (error) {
        console.error('❌ Favorileri alma hatası:', error);
        return [];
    }
};

// Film favorilerde mi kontrol et (geliştirilmiş sürümü kullan)
export const isFavorite = (movieId, movieTitle = null) => {
    const favorites = getFavorites();

    // Önce kesin ID kontrolü (hem yeni hem eski ID formatları)
    const foundById = favorites.some(movie => movie.id === movieId || movie.originalId === movieId);

    if (foundById) {
        return true;
    }

    // DÜZELTME: Basit sayısal ID'ler için ek güvenlik kontrolü
    // Sadece ID ile eşleşme varsa ama film başlığı farklıysa false döndür
    if (typeof movieId === 'number' && movieId <= 100 && movieTitle) {
        const exactMatch = favorites.find(movie =>
            (movie.id === movieId || movie.originalId === movieId) &&
            movie.title === movieTitle
        );

        if (exactMatch) {
            return true;
        }

        // Basit ID eşleşmesi var ama başlık farklıysa false döndür
        const idOnlyMatch = favorites.some(movie => movie.id === movieId || movie.originalId === movieId);
        if (idOnlyMatch) {
            return false;
        }
    }

    // Eski uyumluluk: Sadece basit sayısal ID'ler için false döndür
    if (typeof movieId === 'number' && movieId <= 100 && !movieTitle) {
        return false;
    }

    return false;
};

// Favori film ekleme (geliştirilmiş)
export const addToFavoritesRobust = async (movie) => {
    try {
        const favorites = getFavorites();

        // Benzersiz ID oluştur (ID çakışmasını önlemek için)
        const uniqueId = typeof movie.id === 'string' ? movie.id : `${movie.id}_${movie.title?.replace(/\s/g, '_')}_${Date.now()}`;

        // Zaten favorilerde mi kontrol et (hem ID hem başlık kontrolü)
        const alreadyExists = favorites.some(fav =>
            fav.id === movie.id ||
            fav.originalId === movie.id ||
            (fav.title === movie.title && fav.year === movie.year)
        );

        if (alreadyExists) {
            return false;
        }

        // Favorilere ekle
        const movieWithTimestamp = {
            ...movie,
            id: uniqueId, // Benzersiz ID kullan
            originalId: movie.id, // Orijinal ID'yi saklayalım
            addedToFavoritesAt: new Date().toISOString()
        };

        favorites.push(movieWithTimestamp);
        localStorage.setItem(getFavoritesKey(), JSON.stringify(favorites));

        // Supabase tablosuna kaydet (yeni sistem)
        if (window.currentUserId) {
            await saveFavoriteToSupabase(movieWithTimestamp, window.currentUserId);
        } else {
            // Eski sistem fallback (JSONB preferences)
            saveFavoritesToSupabase(favorites);
        }

        // Global state refresh tetikle
        triggerGlobalRefresh();

        return true;
    } catch (error) {
        console.error('❌ Favorilere ekleme hatası:', error);
        return false;
    }
};

// Favorilerden çıkarma (geliştirilmiş)
export const removeFromFavoritesRobust = async (movieId, movieTitle = null) => {
    try {
        const favorites = getFavorites();

        // Hem ID hem başlık ile eşleşen filmi bul ve çıkar
        const updatedFavorites = favorites.filter(movie => {
            const idMatch = movie.id === movieId || movie.originalId === movieId;
            const titleMatch = movieTitle ? movie.title === movieTitle : false;

            // ID eşleşiyorsa veya hem basit ID hem de başlık eşleşiyorsa çıkar
            return !(idMatch || (typeof movieId === 'number' && movieId <= 100 && titleMatch));
        });

        localStorage.setItem(getFavoritesKey(), JSON.stringify(updatedFavorites));

        // Supabase tablosundan sil (yeni sistem)
        if (window.currentUserId) {
            await removeFavoriteFromSupabase(movieId, movieTitle, window.currentUserId);
        } else {
            // Eski sistem fallback
            saveFavoritesToSupabase(updatedFavorites);
        }

        // Global state refresh tetikle
        triggerGlobalRefresh();

        return true;
    } catch (error) {
        console.error('❌ Favorilerden çıkarma hatası:', error);
        return false;
    }
};

// İzlendi olarak işaretle
export const markAsWatched = async (movie) => {
    try {
        const watched = getWatchedMovies();

        // Zaten izlendi olarak işaretli mi kontrol et (hem ID hem başlık kontrolü)
        if (watched.some(w => w.id === movie.id || (w.title === movie.title && w.year === movie.year))) {
            return false;
        }

        // İzlendi listesine ekle
        const movieWithTimestamp = {
            ...movie,
            watchedAt: new Date().toISOString()
        };

        watched.push(movieWithTimestamp);
        localStorage.setItem(getWatchedKey(), JSON.stringify(watched));

        // Supabase tablosuna kaydet (yeni sistem)
        if (window.currentUserId) {
            await saveWatchedToSupabaseTable(movieWithTimestamp, window.currentUserId);
        } else {
            // Eski sistem fallback
            saveWatchedToSupabaseTable(watched);
        }

        // Global state refresh tetikle
        triggerGlobalRefresh();

        return true;
    } catch (error) {
        console.error('❌ İzlendi işaretleme hatası:', error);
        return false;
    }
};

// İzlenmedi olarak işaretle
export const markAsUnwatched = async (movieId) => {
    try {
        const watched = getWatchedMovies();

        // Hem ID hem de originalId ile kontrol et
        const updatedWatched = watched.filter(movie =>
            movie.id !== movieId && movie.originalId !== movieId
        );

        localStorage.setItem(getWatchedKey(), JSON.stringify(updatedWatched));

        // Supabase tablosundan sil (yeni sistem)
        if (window.currentUserId) {
            await removeWatchedFromSupabase(movieId, window.currentUserId);
        } else {
            // Eski sistem fallback
            saveWatchedToSupabaseTable(updatedWatched);
        }

        // Global state refresh tetikle
        triggerGlobalRefresh();

        return true;
    } catch (error) {
        console.error('❌ İzlenmedi işaretleme hatası:', error);
        return false;
    }
};

// Tüm izlenen filmleri getir (user.id ile kapsamlı)
export const getWatchedMovies = () => {
    try {
        const key = getWatchedKey();
        const watched = localStorage.getItem(key);
        return watched ? JSON.parse(watched) : [];
    } catch (error) {
        console.error('❌ İzlenen filmleri alma hatası:', error);
        return [];
    }
};

// Film izlendi mi kontrol et
export const isWatched = (movieId) => {
    const watched = getWatchedMovies();
    return watched.some(movie => movie.id === movieId || movie.originalId === movieId);
};

// Favori ve izleme istatistikleri
export const getUserStats = () => {
    const favorites = getFavorites();
    const watched = getWatchedMovies();

    return {
        totalFavorites: favorites.length,
        totalWatched: watched.length,
        favoriteGenres: getFavoriteGenres(favorites),
        watchedThisMonth: getWatchedThisMonth(watched),
        recentFavorites: favorites.slice(-5).reverse(), // Son 5 favori
        recentWatched: watched.slice(-5).reverse() // Son 5 izlenen
    };
};

// En çok favorilenen türler
const getFavoriteGenres = (favorites) => {
    const genreCount = {};

    favorites.forEach(movie => {
        const genre = movie.genre;
        genreCount[genre] = (genreCount[genre] || 0) + 1;
    });

    return Object.entries(genreCount)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
        .map(([genre, count]) => ({ genre, count }));
};

// Bu ay izlenen film sayısı
const getWatchedThisMonth = (watched) => {
    const thisMonth = new Date().getMonth();
    const thisYear = new Date().getFullYear();

    return watched.filter(movie => {
        const watchedDate = new Date(movie.watchedAt);
        return watchedDate.getMonth() === thisMonth && watchedDate.getFullYear() === thisYear;
    }).length;
};

// Tüm verileri temizle
export const clearAllFavoriteData = () => {
    try {
        localStorage.removeItem(getFavoritesKey());
        localStorage.removeItem(getWatchedKey());
        return true;
    } catch (error) {
        console.error('❌ Veri temizleme hatası:', error);
        return false;
    }
};

// Verileri dışa aktar
export const exportFavoriteData = () => {
    try {
        const data = {
            favorites: getFavorites(),
            watched: getWatchedMovies(),
            stats: getUserStats(),
            exportDate: new Date().toISOString()
        };

        return data;
    } catch (error) {
        console.error('❌ Veri dışa aktarma hatası:', error);
        return null;
    }
};

// ID çakışması temizleme fonksiyonu (bug fix için)
export const cleanupDuplicateFavorites = () => {
    try {
        const favorites = getFavorites();
        const watched = getWatchedMovies();

        // Sadece basit sayısal ID'leri temizle (1, 2, 3, vb.) - bunlar çakışmaya neden oluyordu
        const cleanedFavorites = favorites.filter(movie => {
            // Eğer ID sadece sayısal değilse (yeni benzersiz ID formatında) tut
            return typeof movie.id === 'string' || movie.id > 100;
        });

        const cleanedWatched = watched.filter(movie => {
            // Eğer ID sadece sayısal değilse (yeni benzersiz ID formatında) tut
            return typeof movie.id === 'string' || movie.id > 100;
        });

        // Temizlenmiş verileri kaydet
        localStorage.setItem(getFavoritesKey(), JSON.stringify(cleanedFavorites));
        localStorage.setItem(getWatchedKey(), JSON.stringify(cleanedWatched));

        const removedFavCount = favorites.length - cleanedFavorites.length;
        const removedWatchedCount = watched.length - cleanedWatched.length;

        return {
            removedFavorites: removedFavCount,
            removedWatched: removedWatchedCount,
            success: true
        };
    } catch (error) {
        console.error('❌ ID çakışması temizleme hatası:', error);
        return { success: false, error: error.message };
    }
};

// YENI: Basit sayısal ID'li eski favorileri temizleme fonksiyonu
export const cleanupLegacyFavorites = () => {
    try {
        const favorites = getFavorites();
        const watched = getWatchedMovies();

        // Sadece basit sayısal ID'leri (1, 2, 3, vb.) temizle
        const cleanedFavorites = favorites.filter(movie => {
            const hasSimpleNumericId = typeof movie.id === 'number' && movie.id <= 100;

            if (hasSimpleNumericId) {
                return false; // Bu filmi listeden çıkar
            }
            return true; // Bu filmi tut
        });

        const cleanedWatched = watched.filter(movie => {
            const hasSimpleNumericId = typeof movie.id === 'number' && movie.id <= 100;

            if (hasSimpleNumericId) {
                return false; // Bu filmi listeden çıkar
            }
            return true; // Bu filmi tut
        });

        // Temizlenmiş verileri kaydet
        localStorage.setItem(getFavoritesKey(), JSON.stringify(cleanedFavorites));
        localStorage.setItem(getWatchedKey(), JSON.stringify(cleanedWatched));

        const removedFavCount = favorites.length - cleanedFavorites.length;
        const removedWatchedCount = watched.length - cleanedWatched.length;

        // Global refresh tetikle
        triggerGlobalRefresh();

        return {
            removedFavorites: removedFavCount,
            removedWatched: removedWatchedCount,
            success: true,
            message: `${removedFavCount} eski favori ve ${removedWatchedCount} izlenen film temizlendi.`
        };
    } catch (error) {
        console.error('❌ Eski favori temizleme hatası:', error);
        return { success: false, error: error.message };
    }
};

// Geliştirilmiş favori kontrol fonksiyonu
export const isFavoriteRobust = (movieId, movieTitle = null) => {
    const favorites = getFavorites();

    // Önce ID ile kontrol et (hem yeni hem eski ID'ler)
    const foundById = favorites.some(movie => movie.id === movieId || movie.originalId === movieId);

    if (foundById) {
        return true;
    }

    // ID ile bulunamazsa ve basit sayısal ID ise başlık kontrolü yap
    if (typeof movieId === 'number' && movieId <= 100 && movieTitle) {
        const foundByTitle = favorites.some(movie => movie.title === movieTitle);
        return foundByTitle;
    }

    return false;
};

// Favori verilerini debug etme fonksiyonu
export const debugFavoriteData = () => {
    const favorites = getFavorites();
    return favorites;
};

// Favori verilerini temizleme (console komutu için)
export const clearAllFavorites = () => {
    if (window.confirm('TÜM FAVORİLERİ SİLMEK İSTEDİĞİNİZDEN EMİN MİSİNİZ? Bu işlem geri alınamaz!')) {
        localStorage.removeItem(getFavoritesKey());
        triggerGlobalRefresh();
        return true;
    }
    return false;
};

// Hızlı favori sıfırlama (senkronizasyon sorunları için)
export const resetFavoriteState = () => {
    const favorites = getFavorites();

    // Temizlenmiş favori listesi oluştur
    const cleanedFavorites = favorites.filter(movie => {
        // Geçerli film verilerini kontrol et
        return movie.title && movie.id;
    });

    // Benzersiz filmleri tut (başlık + yıl bazında)
    const uniqueFavorites = [];
    const seen = new Set();

    cleanedFavorites.forEach(movie => {
        const key = `${movie.title}_${movie.year}`;
        if (!seen.has(key)) {
            seen.add(key);

            // ID'yi düzelt
            if (typeof movie.id === 'number' && movie.id <= 100) {
                movie.id = `cleaned_${movie.title?.replace(/\s/g, '_')}_${Date.now()}`;
                movie.originalId = movie.originalId || movie.id;
            }

            uniqueFavorites.push(movie);
        }
    });

    // Temizlenmiş veriyi kaydet
    localStorage.setItem(getFavoritesKey(), JSON.stringify(uniqueFavorites));
    triggerGlobalRefresh();

    return {
        originalCount: favorites.length,
        cleanedCount: uniqueFavorites.length,
        cleaned: uniqueFavorites
    };
};

// Basit favori kontrolü (karşılaştırma için)
export const isFavoriteSimple = (movieId) => {
    const favorites = getFavorites();
    return favorites.some(movie => movie.id === movieId);
};

// Belirli bir film için favori durumunu test et (debug amaçlı)
export const testMovieFavoriteStatus = (movie) => {
    const result = isFavorite(movie.id, movie.title);
    return result;
};

// Toplam öneri sayısını getir (kaç kez öneri alındığı) - user.id ile kapsamlı
export const getTotalRecommendationCount = () => {
    try {
        const key = getRecommendationsKey();
        const savedRecommendations = localStorage.getItem(key);
        if (!savedRecommendations) return 0;

        const recommendations = JSON.parse(savedRecommendations);

        // Öneri seansı sayısını döndür (film sayısını değil)
        return recommendations.length;
    } catch (error) {
        console.error('❌ Toplam öneri sayısı hesaplama hatası:', error);
        return 0;
    }
};

// Toplam önerilen film sayısını getir - user.id ile kapsamlı
export const getTotalRecommendedMovieCount = () => {
    try {
        const key = getRecommendationsKey();
        const savedRecommendations = localStorage.getItem(key);
        if (!savedRecommendations) return 0;

        const recommendations = JSON.parse(savedRecommendations);

        // Tüm önerilerdeki film sayısını hesapla
        let totalCount = 0;
        recommendations.forEach(recommendation => {
            // Hem eski hem yeni format desteği
            const movies = recommendation.movies || recommendation.recommendations;
            if (movies && Array.isArray(movies)) {
                totalCount += movies.length;
            }
        });

        return totalCount;
    } catch (error) {
        console.error('❌ Toplam film sayısı hesaplama hatası:', error);
        return 0;
    }
};

// Son önerileri getir - user.id ile kapsamlı
export const getRecentRecommendations = (limit = 5) => {
    try {
        const key = getRecommendationsKey();
        const savedRecommendations = localStorage.getItem(key);
        if (!savedRecommendations) return [];

        const recommendations = JSON.parse(savedRecommendations);

        // Son önerileri düzleştir ve tarih sırasına göre sırala
        const allRecommendedMovies = [];

        recommendations.forEach(recommendation => {
            // Hem eski hem yeni format desteği
            const movies = recommendation.movies || recommendation.recommendations;
            if (movies && Array.isArray(movies)) {
                movies.forEach(movie => {
                    allRecommendedMovies.push({
                        ...movie,
                        recommendedAt: recommendation.timestamp || recommendation.createdAt || recommendation.generatedAt || new Date().toISOString(),
                        mood: recommendation.mood || '',
                        reason: recommendation.reason || recommendation.explanation || ''
                    });
                });
            }
        });

        // Tarihe göre sırala ve limite göre döndür
        return allRecommendedMovies
            .sort((a, b) => new Date(b.recommendedAt) - new Date(a.recommendedAt))
            .slice(0, limit);
    } catch (error) {
        console.error('❌ Son öneriler getirme hatası:', error);
        return [];
    }
};

// Öneri istatistiklerini getir - user.id ile kapsamlı
export const getRecommendationStats = () => {
    try {
        const key = getRecommendationsKey();
        const savedRecommendations = localStorage.getItem(key);
        if (!savedRecommendations) {
            return {
                totalRecommendations: 0,
                totalRecommendationSessions: 0,
                averageMoviesPerSession: 0,
                recentRecommendations: []
            };
        }

        const recommendations = JSON.parse(savedRecommendations);
        const totalSessions = recommendations.length;
        let totalMovies = 0;

        recommendations.forEach(recommendation => {
            // Hem eski hem yeni format desteği
            const movies = recommendation.movies || recommendation.recommendations;
            if (movies && Array.isArray(movies)) {
                totalMovies += movies.length;
            }
        });

        return {
            totalRecommendations: totalMovies,
            totalRecommendationSessions: totalSessions,
            averageMoviesPerSession: totalSessions > 0 ? Math.round(totalMovies / totalSessions) : 0,
            recentRecommendations: getRecentRecommendations(3)
        };
    } catch (error) {
        console.error('❌ Öneri istatistikleri hesaplama hatası:', error);
        return {
            totalRecommendations: 0,
            totalRecommendationSessions: 0,
            averageMoviesPerSession: 0,
            recentRecommendations: []
        };
    }
};

// Debug fonksiyonu - localStorage verilerini kontrol et - user.id ile kapsamlı
export const debugRecommendationData = () => {
    try {
        const key = getRecommendationsKey();
        const savedRecommendations = localStorage.getItem(key);
        if (!savedRecommendations) {
            return null;
        }

        const recommendations = JSON.parse(savedRecommendations);
        let totalMovies = 0;
        recommendations.forEach((recommendation) => {
            const movies = recommendation.movies || recommendation.recommendations;
            const movieCount = movies ? movies.length : 0;
            totalMovies += movieCount;
        });

        return { totalSessions: recommendations.length, totalMovies, rawData: recommendations };
    } catch (error) {
        console.error('🔍 DEBUG: Veri okuma hatası:', error);
        return null;
    }
};

// Global state refresh sistemi (sonsuz döngü koruması ile)
let globalRefreshCallbacks = [];
let isRefreshing = false; // Sonsuz döngü koruması
let refreshDebounceTimer = null;

export const registerGlobalRefresh = (callback) => {
    globalRefreshCallbacks.push(callback);
    return () => {
        globalRefreshCallbacks = globalRefreshCallbacks.filter(cb => cb !== callback);
    };
};

const triggerGlobalRefresh = () => {
    // Sonsuz döngü koruması
    if (isRefreshing) {
        return;
    }

    // Debounce ile çok hızlı çağrıları engelle
    if (refreshDebounceTimer) {
        clearTimeout(refreshDebounceTimer);
    }

    refreshDebounceTimer = setTimeout(() => {
        isRefreshing = true;

        globalRefreshCallbacks.forEach(callback => {
            try {
                callback();
            } catch (error) {
                console.error('Global refresh callback error:', error);
            }
        });

        // Refresh tamamlandı
        setTimeout(() => {
            isRefreshing = false;
        }, 100);
    }, 50); // 50ms debounce
};

// Emergency stop fonksiyonu
export const stopGlobalRefresh = () => {
    isRefreshing = false;
    if (refreshDebounceTimer) {
        clearTimeout(refreshDebounceTimer);
        refreshDebounceTimer = null;
    }
};

// Global refresh durumunu sıfırla
export const resetGlobalRefreshState = () => {
    globalRefreshCallbacks = [];
    isRefreshing = false;
    if (refreshDebounceTimer) {
        clearTimeout(refreshDebounceTimer);
        refreshDebounceTimer = null;
    }
};

// Veri tutarlılığı kontrolü ve onarımı
export const fixFavoriteDataConsistency = () => {
    try {
        const favorites = getFavorites();

        // Duplicate kontrolü (aynı film birden fazla kez favorite edilmiş)
        const uniqueFavorites = [];
        const seenMovies = new Set();

        favorites.forEach(movie => {
            const movieKey = `${movie.title}_${movie.year}`;

            if (!seenMovies.has(movieKey)) {
                seenMovies.add(movieKey);

                // ID problemlerini düzelt
                if (typeof movie.id === 'number' && movie.id <= 100) {
                    movie.id = `${movie.id}_${movie.title?.replace(/\s/g, '_')}_${Date.now()}_fixed`;
                    movie.originalId = movie.originalId || movie.id;
                }

                uniqueFavorites.push(movie);
            }
        });

        // Temizlenmiş veriyi kaydet
        localStorage.setItem(getFavoritesKey(), JSON.stringify(uniqueFavorites));

        triggerGlobalRefresh();

        return {
            originalCount: favorites.length,
            cleanedCount: uniqueFavorites.length,
            removedDuplicates: favorites.length - uniqueFavorites.length
        };

    } catch (error) {
        console.error('❌ Veri tutarlılığı düzeltme hatası:', error);
        return null;
    }
}; 